import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { comics } from "@/mocks/comics";
import { categories } from "@/mocks/categories";
import { useDownloadNotice } from "@/components/feature/DownloadNoticeProvider";

const categoryName = (slug: string) => categories.find((item) => item.slug === slug)?.name ?? slug;

export default function HotCarousel() {
  const hot = useMemo(() => [...comics].sort((a, b) => b.rating - a.rating).slice(0, 6), []);
  const [index, setIndex] = useState(0);
  const { show } = useDownloadNotice();

  useEffect(() => {
    const timer = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % hot.length);
    }, 5000);
    return () => window.clearInterval(timer);
  }, [hot.length]);

  const current = hot[index];

  const goPrev = () => setIndex((prev) => (prev - 1 + hot.length) % hot.length);
  const goNext = () => setIndex((prev) => (prev + 1) % hot.length);

  return (
    <section className="w-full">
      <div className="mb-4 flex items-end justify-between gap-4">
        <div>
          <h2 className="font-heading text-xl font-extrabold text-foreground-950 md:text-2xl">热门推荐</h2>
          <p className="mt-1.5 text-sm text-foreground-600">评分最高的精选作品，值得先收藏</p>
        </div>
        <div className="hidden items-center gap-2 sm:flex">
          <button
            type="button"
            onClick={goPrev}
            aria-label="上一个"
            className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border border-background-300 text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-600"
          >
            <i className="ri-arrow-left-s-line text-xl" />
          </button>
          <button
            type="button"
            onClick={goNext}
            aria-label="下一个"
            className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border border-background-300 text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-600"
          >
            <i className="ri-arrow-right-s-line text-xl" />
          </button>
        </div>
      </div>

      <div className="overflow-hidden rounded-lg border border-primary-900/20 bg-primary-950">
        <div className="relative flex flex-col gap-6 p-6 md:flex-row md:items-center md:gap-10 md:p-9">
          <div key={current.id} className="flex-1 animate-fade-in">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-accent-500/15 px-3 py-1 text-xs font-medium text-accent-400">
              <i className="ri-fire-fill" />
              热门推荐
            </span>
            <h3 className="mt-4 font-heading text-xl font-extrabold text-background-50 md:text-3xl">{current.title}</h3>
            <p className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-primary-200/85">
              <span className="flex items-center gap-1">
                <i className="ri-user-3-line" />
                {current.author}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-map-pin-line" />
                {current.region}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-bookmark-line" />
                {current.categories.map(categoryName).join(" · ")}
              </span>
              <span className="flex items-center gap-1 text-accent-400">
                <i className="ri-star-fill" />
                {current.rating.toFixed(1)}
              </span>
            </p>
            <p className="mt-4 line-clamp-3 max-w-xl text-sm leading-relaxed text-primary-100/80">
              {current.summary}
            </p>

            <div className="mt-6 flex flex-wrap items-center gap-3">
              <Link
                to={`/detail/${current.id}`}
                className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-background-50 px-5 py-2.5 text-sm font-semibold text-primary-950 transition-colors hover:bg-primary-100"
              >
                <i className="ri-book-open-line" />
                查看详情
              </Link>
              <button
                type="button"
                onClick={() => show(`${current.title} · 共 ${current.chapters.length} 卷合集（ZIP / PDF）`)}
                className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-accent-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-accent-600"
              >
                <i className="ri-download-2-line" />
                立即下载
              </button>
            </div>
          </div>

          <Link
            to={`/detail/${current.id}`}
            className="relative mx-auto block w-40 shrink-0 overflow-hidden rounded-lg md:mx-0 md:w-52"
            style={{ aspectRatio: "3 / 4" }}
          >
            <img
              src={current.cover}
              alt={`${current.title} 漫画封面`}
              className="h-full w-full object-cover object-top"
            />
          </Link>
        </div>

        <div className="flex items-center justify-center gap-2 border-t border-background-50/10 py-4">
          {hot.map((comic, dotIndex) => (
            <button
              key={comic.id}
              type="button"
              aria-label={`查看第 ${dotIndex + 1} 个推荐`}
              onClick={() => setIndex(dotIndex)}
              className={`cursor-pointer rounded-full transition-all ${
                dotIndex === index ? "h-2 w-6 bg-accent-500" : "h-2 w-2 bg-background-50/30 hover:bg-background-50/60"
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}